
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '../contexts/AuthContext';

interface AttendanceData {
  monthlyData: Array<{
    month: string;
    attended: number;
    missed: number;
    rate: number;
  }>;
  overallData: Array<{
    name: string;
    value: number;
    color: string;
  }>;
  totalAttended: number;
  totalMissed: number;
  attendanceRate: number;
}

export const useStudentAttendance = () => {
  const { profile } = useAuth();
  const [attendanceData, setAttendanceData] = useState<AttendanceData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (profile?.id) {
      fetchAttendanceData();
    }
  }, [profile?.id]);

  const fetchAttendanceData = async () => {
    if (!profile?.id) return;

    try {
      setIsLoading(true);

      // Fetch attendance records for the last 6 months
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

      const { data: attendanceRecords, error } = await supabase
        .from('session_attendance')
        .select(`
          attended,
          created_at,
          training_sessions!inner(date, title)
        `)
        .eq('student_id', profile.id)
        .gte('training_sessions.date', sixMonthsAgo.toISOString().split('T')[0])
        .order('training_sessions.date', { ascending: true });

      if (error) {
        console.error('Error fetching attendance:', error);
        return;
      }

      // Process monthly data
      const monthlyStats: { [key: string]: { attended: number; missed: number } } = {};
      let totalAttended = 0;
      let totalMissed = 0;

      attendanceRecords?.forEach((record) => {
        const date = new Date(record.training_sessions?.date || record.created_at);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}`;

        if (!monthlyStats[monthKey]) {
          monthlyStats[monthKey] = { attended: 0, missed: 0 };
        }

        if (record.attended) {
          monthlyStats[monthKey].attended++;
          totalAttended++;
        } else {
          monthlyStats[monthKey].missed++;
          totalMissed++;
        }
      });

      // Convert to array format for charts
      const monthlyData = Object.entries(monthlyStats).map(([month, stats]) => {
        const total = stats.attended + stats.missed;
        const rate = total > 0 ? Math.round((stats.attended / total) * 100) : 0;
        return {
          month,
          attended: stats.attended,
          missed: stats.missed,
          rate
        };
      });

      const totalSessions = totalAttended + totalMissed;
      const attendanceRate = totalSessions > 0 ? Math.round((totalAttended / totalSessions) * 100) : 0;

      const overallData = [
        { name: 'Attended', value: totalAttended, color: '#dc2626' },
        { name: 'Missed', value: totalMissed, color: '#6b7280' },
      ];

      setAttendanceData({
        monthlyData,
        overallData,
        totalAttended,
        totalMissed,
        attendanceRate
      });
    } catch (error) {
      console.error('Error processing attendance data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return { attendanceData, isLoading, refetch: fetchAttendanceData };
};
